'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { useDrawerPortalContainer } from '@repo/ui/components/ui/drawer-portal-context';
import { cn } from '@repo/ui/lib/utils';
import { Button } from '@repo/ui/components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@repo/ui/components/ui/input-group';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
const Combobox = ComboboxPrimitive.Root;
function ComboboxChip(t0) {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      className,
      showRemove: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const showRemove = t1 === undefined ? true : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("flex h-[calc(--spacing(5.25))] w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs font-medium whitespace-nowrap text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== showRemove) {
    t3 = showRemove && <ComboboxPrimitive.ChipRemove className="-ml-1 opacity-50 hover:opacity-100" data-slot="combobox-chip-remove" render={<Button size="icon-xs" variant="ghost" />}><XIcon className="pointer-events-none" /></ComboboxPrimitive.ChipRemove>;
    $[7] = showRemove;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== t2 || $[12] !== t3) {
    t4 = <ComboboxPrimitive.Chip className={t2} data-slot="combobox-chip" {...props} data-sentry-element="ComboboxPrimitive.Chip" data-sentry-component="ComboboxChip" data-sentry-source-file="combobox.tsx">{children}{t3}</ComboboxPrimitive.Chip>;
    $[9] = children;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function ComboboxChips(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex min-h-8 flex-wrap items-center gap-1 rounded-lg border border-input bg-transparent bg-clip-padding px-2.5 py-1 text-sm transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 has-aria-invalid:border-destructive has-aria-invalid:ring-3 has-aria-invalid:ring-destructive/20 has-data-[slot=combobox-chip]:px-1 dark:bg-input/30 dark:has-aria-invalid:border-destructive/50 dark:has-aria-invalid:ring-destructive/40", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Chips className={t1} data-slot="combobox-chips" {...props} data-sentry-element="ComboboxPrimitive.Chips" data-sentry-component="ComboboxChips" data-sentry-source-file="combobox.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxChipsInput(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("min-w-16 flex-1 outline-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Input className={t1} data-slot="combobox-chip-input" {...props} data-sentry-element="ComboboxPrimitive.Input" data-sentry-component="ComboboxChipsInput" data-sentry-source-file="combobox.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxClear(t0) {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <InputGroupButton size="icon-xs" variant="ghost" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <XIcon className="pointer-events-none" data-sentry-element="XIcon" data-sentry-source-file="combobox.tsx" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== t1) {
    t4 = <ComboboxPrimitive.Clear className={t1} data-slot="combobox-clear" render={t2} {...props} data-sentry-element="ComboboxPrimitive.Clear" data-sentry-component="ComboboxClear" data-sentry-source-file="combobox.tsx">{t3}</ComboboxPrimitive.Clear>;
    $[7] = props;
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function ComboboxCollection(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} data-sentry-element="ComboboxPrimitive.Collection" data-sentry-component="ComboboxCollection" data-sentry-source-file="combobox.tsx" />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ComboboxContent(t0) {
  const $ = _c(24);
  let anchor;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      align: t1,
      alignOffset: t2,
      anchor,
      className,
      side: t3,
      sideOffset: t4,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = anchor;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    anchor = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const align = t1 === undefined ? "start" : t1;
  const alignOffset = t2 === undefined ? 0 : t2;
  const side = t3 === undefined ? "bottom" : t3;
  const sideOffset = t4 === undefined ? 6 : t4;
  const drawerContainer = useDrawerPortalContainer();
  let t5;
  if ($[8] !== className) {
    t5 = cn("group/combobox-content relative max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) overflow-hidden rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[chips=true]:min-w-(--anchor-width) data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/30 *:data-[slot=input-group]:shadow-none", className);
    $[8] = className;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = !!anchor;
  let t7;
  if ($[10] !== props || $[11] !== t5 || $[12] !== t6) {
    t7 = <ComboboxPrimitive.Popup className={t5} data-chips={t6} data-slot="combobox-content" {...props} data-sentry-element="ComboboxPrimitive.Popup" data-sentry-source-file="combobox.tsx" />;
    $[10] = props;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== align || $[15] !== alignOffset || $[16] !== anchor || $[17] !== side || $[18] !== sideOffset || $[19] !== t7) {
    t8 = <ComboboxPrimitive.Positioner align={align} alignOffset={alignOffset} anchor={anchor} className="isolate z-60" side={side} sideOffset={sideOffset} data-sentry-element="ComboboxPrimitive.Positioner" data-sentry-source-file="combobox.tsx">{t7}</ComboboxPrimitive.Positioner>;
    $[14] = align;
    $[15] = alignOffset;
    $[16] = anchor;
    $[17] = side;
    $[18] = sideOffset;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== drawerContainer || $[22] !== t8) {
    t9 = <ComboboxPrimitive.Portal container={drawerContainer} data-sentry-element="ComboboxPrimitive.Portal" data-sentry-component="ComboboxContent" data-sentry-source-file="combobox.tsx">{t8}</ComboboxPrimitive.Portal>;
    $[21] = drawerContainer;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
function ComboboxEmpty(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("hidden w-full justify-center py-2 text-center text-sm text-muted-foreground group-data-empty/combobox-content:flex", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Empty className={t1} data-slot="combobox-empty" {...props} data-sentry-element="ComboboxPrimitive.Empty" data-sentry-component="ComboboxEmpty" data-sentry-source-file="combobox.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Group className={t1} data-slot="combobox-group" {...props} data-sentry-element="ComboboxPrimitive.Group" data-sentry-component="ComboboxGroup" data-sentry-source-file="combobox.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxInput(t0) {
  const $ = _c(28);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      children,
      className,
      disabled: t1,
      showClear: t2,
      showTrigger: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const disabled = t1 === undefined ? false : t1;
  const showClear = t2 === undefined ? false : t2;
  const showTrigger = t3 === undefined ? true : t3;
  let t4;
  if ($[7] !== className) {
    t4 = cn("w-auto", className);
    $[7] = className;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== disabled) {
    t5 = <InputGroupInput disabled={disabled} />;
    $[9] = disabled;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== props || $[12] !== t5) {
    t6 = <ComboboxPrimitive.Input render={t5} {...props} data-sentry-element="ComboboxPrimitive.Input" data-sentry-source-file="combobox.tsx" />;
    $[11] = props;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== disabled || $[15] !== showTrigger) {
    t7 = showTrigger && <InputGroupButton className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent" data-slot="input-group-button" disabled={disabled} render={<ComboboxTrigger />} size="icon-xs" variant="ghost" />;
    $[14] = disabled;
    $[15] = showTrigger;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== disabled || $[18] !== showClear) {
    t8 = showClear && <ComboboxClear disabled={disabled} />;
    $[17] = disabled;
    $[18] = showClear;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7 || $[21] !== t8) {
    t9 = <InputGroupAddon align="inline-end" data-sentry-element="InputGroupAddon" data-sentry-source-file="combobox.tsx">{t7}{t8}</InputGroupAddon>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== children || $[24] !== t4 || $[25] !== t6 || $[26] !== t9) {
    t10 = <InputGroup className={t4} data-sentry-element="InputGroup" data-sentry-component="ComboboxInput" data-sentry-source-file="combobox.tsx">{t6}{t9}{children}</InputGroup>;
    $[23] = children;
    $[24] = t4;
    $[25] = t6;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function ComboboxItem(t0) {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex w-full cursor-default items-center gap-2 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-accent data-highlighted:text-accent-foreground not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ComboboxPrimitive.ItemIndicator render={t2} data-sentry-element="ComboboxPrimitive.ItemIndicator" data-sentry-source-file="combobox.tsx"><CheckIcon className="pointer-events-none" data-sentry-element="CheckIcon" data-sentry-source-file="combobox.tsx" /></ComboboxPrimitive.ItemIndicator>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children || $[9] !== props || $[10] !== t1) {
    t4 = <ComboboxPrimitive.Item className={t1} data-slot="combobox-item" {...props} data-sentry-element="ComboboxPrimitive.Item" data-sentry-component="ComboboxItem" data-sentry-source-file="combobox.tsx">{children}{t3}</ComboboxPrimitive.Item>;
    $[8] = children;
    $[9] = props;
    $[10] = t1;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function ComboboxLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-2 py-1.5 text-xs text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.GroupLabel className={t1} data-slot="combobox-label" {...props} data-sentry-element="ComboboxPrimitive.GroupLabel" data-sentry-component="ComboboxLabel" data-sentry-source-file="combobox.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxList(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("no-scrollbar max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto overscroll-contain p-1 data-empty:p-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.List className={t1} data-slot="combobox-list" {...props} data-sentry-element="ComboboxPrimitive.List" data-sentry-component="ComboboxList" data-sentry-source-file="combobox.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Separator className={t1} data-slot="combobox-separator" {...props} data-sentry-element="ComboboxPrimitive.Separator" data-sentry-component="ComboboxSeparator" data-sentry-source-file="combobox.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxTrigger(t0) {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("[&_svg:not([class*='size-'])]:size-4", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDownIcon className="pointer-events-none size-4 text-muted-foreground" data-sentry-element="ChevronDownIcon" data-sentry-source-file="combobox.tsx" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t1) {
    t3 = <ComboboxPrimitive.Trigger className={t1} data-slot="combobox-trigger" {...props} data-sentry-element="ComboboxPrimitive.Trigger" data-sentry-component="ComboboxTrigger" data-sentry-source-file="combobox.tsx">{children}{t2}</ComboboxPrimitive.Trigger>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function ComboboxValue(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <ComboboxPrimitive.Value data-slot="combobox-value" {...props} data-sentry-element="ComboboxPrimitive.Value" data-sentry-component="ComboboxValue" data-sentry-source-file="combobox.tsx" />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function useComboboxAnchor() {
  return React.useRef(null);
}
export { Combobox, ComboboxChip, ComboboxChips, ComboboxChipsInput, ComboboxCollection, ComboboxContent, ComboboxEmpty, ComboboxGroup, ComboboxInput, ComboboxItem, ComboboxLabel, ComboboxList, ComboboxSeparator, ComboboxTrigger, ComboboxValue, useComboboxAnchor };